import apiClient from './apiClient';

export type DrawdownPacketStatus =
  | 'snapshot_created'
  | 'submitted'
  | 'approved'
  | 'rejected'
  | 'paid'
  | 'voided';

export interface DrawdownPacketRow {
  id: string;
  projectId: string;
  drawdownReference: string;
  status: DrawdownPacketStatus;
  snapshotVersion: number;
  snapshotGeneratedAt: string;
  grossAmount: number;
  retentionAmount: number;
  netAmount: number;
  itemCount: number;
  groupCount: number;
  pdfDocumentId: string | null;
  pdfFileName: string | null;
  cosignRequired: boolean;
  cosignInitiatedAt: string | null;
  cosignCompletedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  voidReason: string | null;
  createdAt: string;
  updatedAt: string;
  project?: { id: string; name: string; projectCode?: string };
  pdfDocument?: { id: string; title: string; fileUrl: string; status: string };
}

export interface DrawdownPacketWithItems extends DrawdownPacketRow {
  items?: Array<{
    id: string;
    description: string;
    drawdownReference: string | null;
    status: string;
    grossAmount: number;
    retentionAmount: number;
    netAmount: number;
    task?: { id: string; taskCode: string; name?: string; paymentStatus?: string };
    qsCertificateLine?: {
      id: string;
      lineCode: string;
      description: string;
      status: string;
      percentCertified: number;
      certifiedValue: number;
      retentionAmount: number;
      netCertifiedValue: number;
    };
  }>;
}

export interface DrawdownPacketList {
  packets: DrawdownPacketRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

type ApiEnvelope<T> = { success?: boolean; data?: T };

const unwrap = <T>(response: unknown): T => {
  const body = response as ApiEnvelope<T> | T;
  return body && typeof body === 'object' && Object.prototype.hasOwnProperty.call(body, 'data')
    ? ((body as ApiEnvelope<T>).data as T)
    : (body as T);
};

class DrawdownService {
  async listPackets(filters: {
    projectId?: string;
    status?: DrawdownPacketStatus;
    page?: number;
    limit?: number;
  } = {}): Promise<DrawdownPacketList> {
    const params: Record<string, string> = {};
    if (filters.projectId) params.projectId = filters.projectId;
    if (filters.status) params.status = filters.status;
    if (filters.page) params.page = String(filters.page);
    if (filters.limit) params.limit = String(filters.limit);
    const response: any = await apiClient.get('/drawdown-packets', { params });
    return unwrap<DrawdownPacketList>(response) || {
      packets: [],
      total: 0,
      page: filters.page || 1,
      limit: filters.limit || 25,
      totalPages: 0,
    };
  }

  async getPacket(id: string): Promise<DrawdownPacketWithItems | null> {
    const response: any = await apiClient.get(`/drawdown-packets/${id}`);
    return unwrap<DrawdownPacketWithItems | null>(response);
  }

  /**
   * Lifecycle actions — backend already enforces role + state checks.
   * Each returns the updated packet so callers can refresh in place.
   */
  async submitInitiate(id: string): Promise<DrawdownPacketRow> {
    const response: any = await apiClient.post(`/drawdown-packets/${id}/submit-initiate`);
    return unwrap<DrawdownPacketRow>(response);
  }

  async submitCosign(id: string): Promise<DrawdownPacketRow> {
    const response: any = await apiClient.post(`/drawdown-packets/${id}/submit-cosign`);
    return unwrap<DrawdownPacketRow>(response);
  }

  async markPaid(id: string, paidAt?: string): Promise<DrawdownPacketRow> {
    const body = paidAt ? { paidAt } : {};
    const response: any = await apiClient.post(`/drawdown-packets/${id}/mark-paid`, body);
    return unwrap<DrawdownPacketRow>(response);
  }
}

export default new DrawdownService();
